import { useEffect, useId, useRef } from 'react';
import { formatActivityDate, type ActivityCalendar } from '../progress/activity';

function completionLabel(count: number): string {
  return `${count} completed curriculum ${count === 1 ? 'day' : 'days'}`;
}

export function ActivityCalendarView({ calendar }: { calendar: ActivityCalendar }) {
  const descriptionId = useId();
  const scroller = useRef<HTMLDivElement>(null);
  const calendarDays = calendar.weeks.flatMap((week) => week.days);
  const activeDays = calendarDays.filter((day) => !day.future && day.count > 0);

  useEffect(() => {
    const element = scroller.current;
    if (element) element.scrollLeft = element.scrollWidth;
  }, []);

  return (
    <figure className="activity-figure">
      <div
        className="activity-scroll"
        ref={scroller}
        role="region"
        tabIndex={0}
        aria-label="Completion activity calendar"
        aria-describedby={descriptionId}
      >
        <div className="activity-calendar" aria-hidden="true">
          <div
            className="activity-months"
            style={{ gridTemplateColumns: `repeat(${calendar.weeks.length}, var(--activity-cell))` }}
          >
            {calendar.months.map((month) => (
              <span
                key={`${month.label}-${month.startWeek}`}
                style={{ gridColumn: `${month.startWeek + 1} / span ${month.span}` }}
              >
                {month.label}
              </span>
            ))}
          </div>

          <div className="activity-body">
            <div className="activity-weekdays">
              <span>Mon</span>
              <span />
              <span>Wed</span>
              <span />
              <span>Fri</span>
              <span />
              <span />
            </div>
            <div className="activity-week-grid">
              {calendarDays.map((day) => {
                const title = day.future
                  ? `${formatActivityDate(day.dateKey)} is ahead`
                  : `${completionLabel(day.count)} on ${formatActivityDate(day.dateKey)}`;
                return (
                  <span
                    className={`activity-cell activity-cell--${day.future ? 'future' : day.level}`}
                    key={day.dateKey}
                    title={title}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="sr-only">
        <p>Calendar range: {calendar.rangeLabel}.</p>
        {activeDays.length > 0 ? (
          <ul>
            {activeDays.map((day) => (
              <li key={day.dateKey}>{formatActivityDate(day.dateKey)}: {completionLabel(day.count)}.</li>
            ))}
          </ul>
        ) : (
          <p>No dated completions have been recorded in this range.</p>
        )}
      </div>

      <figcaption id={descriptionId}>
        <span>{completionLabel(calendar.completionsInRange)} across {calendar.activeDaysInRange} active {calendar.activeDaysInRange === 1 ? 'date' : 'dates'}.</span>
        <span className="activity-legend" aria-label="Activity shade: no completions to four or more completions">
          Less
          {[0, 1, 2, 3, 4].map((level) => (
            <span className={`activity-cell activity-cell--${level}`} key={level} aria-hidden="true" />
          ))}
          More
        </span>
      </figcaption>
    </figure>
  );
}
